import React, { useEffect, useState } from 'react';
import { Modal, Select, Button, message, Steps, Spin, Card, Typography, List, Tag, Collapse, Empty, Alert, Row, Col, Avatar, Space } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import ApiService from '../../services/api';
import { BankUpdateReportDTOUpdate, BankChangeDTOUpdate } from '../../types/api.types';
import BinanceIcon from '../../assets/icons/binance-svgrepo-com.svg';
import BybitIcon from '../../assets/icons/bybit-svgrepo-com.svg';

const { Option } = Select;
const { Text, Title } = Typography;
const { Panel } = Collapse;

interface UpdateBanksModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ExchangeInfo {
  name: string;
  lastUpdate: string | null;
}

const UpdateBanksModal: React.FC<UpdateBanksModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [step, setStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exchanges, setExchanges] = useState<ExchangeInfo[]>([]);
  const [selectedExchange, setSelectedExchange] = useState<string | null>(null);
  const [reportMethods, setReportMethods] = useState<BankUpdateReportDTOUpdate[]>([]);
  const [fiats, setFiats] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      loadExchanges();
      setStep(0);
      setSelectedExchange(null);
      setReportMethods([]);
    }
  }, [visible]);

  const loadExchanges = async () => {
    setLoading(true);
    try {
      const names = await ApiService.getNameExchanges();
      const exchangeInfos = await Promise.all(names.map(async (name) => {
        try {
          const date = await ApiService.getExchangeDateUpdate(name);
          return { name, lastUpdate: date };
        } catch (e) {
          console.error(`Error loading date for ${name}`, e);
          return { name, lastUpdate: null };
        }
      }));
      setExchanges(exchangeInfos);
    } catch (error) {
      console.error('Ошибка загрузки бирж:', error);
      message.error('Не удалось загрузить список бирж');
    } finally {
      setLoading(false);
    }
  };

  const getDaysSince = (dateStr: string | null): number => {
    if (!dateStr) return -1;
    // Предполагаем, что дата приходит в UTC. Добавляем 'Z', если его нет, чтобы парсилось как UTC
    const normalizedDate = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;
    const date = new Date(normalizedDate);
    const now = new Date();
    
    const diffTime = now.getTime() - date.getTime();
    
    // Если дата в будущем (например, из-за рассинхрона часов), вернем 0
    if (diffTime < 0) return 0;
    
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
  };

  const getCardStyle = (days: number): React.CSSProperties => {
    if (days >= 0 && days <= 14) {
      return { backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }; // Green
    } else if (days > 14 && days <= 28) {
      return { backgroundColor: '#fffbe6', borderColor: '#ffe58f' }; // Yellow
    } else {
      return { backgroundColor: '#fff1f0', borderColor: '#ffa39e' }; // Red
    }
  };

  const getExchangeIcon = (exchangeName: string) => {
    const name = exchangeName.toLowerCase();
    if (name.includes('binance')) {
      return <Avatar size={64} src={BinanceIcon} style={{ backgroundColor: 'transparent' }} />;
    } else if (name.includes('bybit')) {
      return <Avatar size={64} src={BybitIcon} style={{ backgroundColor: 'transparent' }} />;
    }
    return <Avatar size={64} icon={<BankOutlined />} style={{ backgroundColor: 'rgba(0,0,0,0.1)', color: '#000' }} />;
  };

  const handleExchangeSelect = async (exchangeName: string) => {
    setSelectedExchange(exchangeName);
    setLoading(true);
    try {
      const exchangeFiats = await ApiService.getFiatsFromExchangeInfo(exchangeName);
      setFiats(exchangeFiats);
      const report = await ApiService.updateBankFiatPreview(exchangeName, exchangeFiats);
      setReportMethods(report);
      setStep(1);
    } catch (error) {
      console.error('Ошибка получения данных для обновления:', error);
      message.error('Ошибка при получении данных');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedExchange) return;

    setLoading(true);
    try {
      await ApiService.updateBankFiatApply(selectedExchange, fiats);
      message.success('Банки успешно обновлены');
      onSuccess();
      onCancel();
    } catch (error) {
      console.error('Ошибка применения изменений:', error);
      message.error('Не удалось применить изменения');
    } finally {
      setLoading(false);
    }
  };

  const hasAnyChanges = (item: BankUpdateReportDTOUpdate) => {
    return (
      item.added_banks.length > 0 || 
      item.removed_banks.length > 0 || 
      item.updated_banks.length > 0 ||
      item.delited_fiats ||
      item.deleted_soups.length > 0 ||
      item.deleted_parsers_from_soups_soups_name.length > 0
    );
  };

  const renderChanges = (item: BankUpdateReportDTOUpdate) => {
    if (!hasAnyChanges(item)) {
      return <Text type="secondary">Нет изменений</Text>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {item.delited_fiats && (
          <Alert
            message="Внимание!"
            description="Валюта будет полностью удалена, так как в ней не останется ни одного супа."
            type="error"
            showIcon
          />
        )}

        {item.deleted_soups.length > 0 && (
          <Alert
            message="Удаление супов"
            description={
              <div>
                <Text>Следующие супы будут удалены, так как в них не останется бирж:</Text>
                <div style={{ marginTop: 8, maxHeight: 100, overflowY: 'auto' }}>
                  {item.deleted_soups.map(soup => (
                    <div key={soup}>• <Text strong>{soup}</Text></div>
                  ))}
                </div>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {item.deleted_parsers_from_soups_soups_name.length > 0 && (
          <Alert
             message={`Удаление биржи ${selectedExchange} из супов`}
             description={
               <div>
                  <Text>Биржа будет удалена из следующих супов, так как в ней не останется банков:</Text>
                  <div style={{ marginTop: 8, maxHeight: 100, overflowY: 'auto' }}>
                    {item.deleted_parsers_from_soups_soups_name.map(soupName => (
                      <div key={soupName}>• <Text strong>{soupName}</Text></div>
                    ))}
                  </div>
               </div>
             }
             type="warning"
             showIcon
          />
        )}

        {item.added_banks.length > 0 && (
          <div>
            <Text strong type="success">Добавлены банки:</Text>
            <List
              size="small"
              dataSource={item.added_banks}
              renderItem={bank => <List.Item>{bank.name}</List.Item>}
            />
          </div>
        )}
        
        {item.removed_banks.length > 0 && (
          <div>
            <Text strong type="danger">Удалены банки:</Text>
            <List
              size="small"
              dataSource={item.removed_banks}
              renderItem={bank => <List.Item>{bank.name}</List.Item>}
            />
          </div>
        )}

        {item.updated_banks.length > 0 && (
          <div>
            <Text strong type="warning">Изменены:</Text>
            <List
              size="small"
              dataSource={item.updated_banks}
              renderItem={change => {
                const renderChangeDetails = (c: BankChangeDTOUpdate) => {
                  if (c.change_type === 'rename') {
                    return (
                      <span>
                        Переименование: <Text delete>{c.old.name}</Text> → <Text strong>{c.new.name}</Text>
                      </span>
                    );
                  } else if (c.change_type === 'code_change') {
                    return (
                      <span>
                         Смена кода ({c.new.name}): <Text delete>{c.old.codename}</Text> → <Text strong>{c.new.codename}</Text>
                      </span>
                    );
                  }
                  return <span>{c.new.name} (Тип изменения: {c.change_type})</span>;
                };
                return <List.Item>{renderChangeDetails(change)}</List.Item>;
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title="Обновление банков"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Steps current={step} style={{ marginBottom: 24 }}>
        <Steps.Step title="Выбор биржи" />
        <Steps.Step title="Подтверждение изменений" />
      </Steps>

      {step === 0 && (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px' }}>
          {loading ? (
             <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
          ) : (
             <Row gutter={[16, 16]}>
              {exchanges.map((ex) => {
                const days = getDaysSince(ex.lastUpdate);
                const style = getCardStyle(days);
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={ex.name}>
                    <Card
                      hoverable
                      style={{ ...style, cursor: 'pointer', textAlign: 'center' }}
                      onClick={() => handleExchangeSelect(ex.name)}
                    >
                      <Space direction="vertical" align="center">
                        {getExchangeIcon(ex.name)}
                        <Title level={4} style={{ margin: '10px 0 5px' }}>{ex.name}</Title>
                        <Text type="secondary">
                           {ex.lastUpdate ? (days === 0 ? 'Сегодня' : `${days} дн. назад`) : 'Никогда'}
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
             <Button onClick={onCancel}>Отмена</Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
          ) : (
            <>
              <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: 16 }}>
                {reportMethods.length === 0 ? (
                    <Empty description="Нет данных для отображения" />
                ) : (
                  <Collapse defaultActiveKey={reportMethods.flatMap((r, i) => hasAnyChanges(r) ? [i] : [])}>
                    {reportMethods.map((report, index) => (
                      <Panel 
                        header={`${report.fiat_name} `} 
                        key={index}
                        extra={
                           hasAnyChanges(report) ? 
                           <Tag color="warning">Есть изменения</Tag> : <Tag color="success">Нет изменений</Tag>
                        }
                      >
                       {renderChanges(report)}
                      </Panel>
                    ))}
                  </Collapse>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={onCancel}>Отменить</Button>
                <Button type="primary" onClick={handleApply}>Применить</Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default UpdateBanksModal;
